<template>
    <v-card>
        <v-card-text>
            <v-form data-cy="products-open-close-room-form">
                <v-row>
                    <v-col cols="12" md="4">
                        <date-range-m-extended
                            :popover="{placement: 'bottom'}"
                            :show-icon="false"
                            :single-line="false"
                            :_start="model.dateFrom"
                            :_end="model.dateTo"
                            @change-start="model.dateFrom = $event"
                            @change-end="model.dateTo = $event"
                        />
                    </v-col>
                    <v-col cols="12" md="8">
                        <div class="d-flex align-center">
                            <days-picker v-model="model.dayOfWeek" />
                            <v-select
                                v-model="model.tariffId"
                                class="ms-4"
                                clearable
                                hide-details
                                :label="$t('tariff')"
                                :items="tariffs"
                                item-title="name"
                                max-width="240"
                                item-value="id"
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-row v-if="model.services.length" class="mt-3">
                    <v-col cols="12" md="12">
                        <h3>
                            {{ $t('supplier.open_close_rooms_title') }}
                        </h3>

                        <!-- Tree view when room groups are available -->
                        <div v-if="basicRoomGroups.length > 0" class="room-types-tree" style="width: 50%">
                            <template
                                v-for="(basicRoomGroup, basicRoomIndex) in basicRoomGroups"
                                :key="basicRoomGroup.basicRoomId"
                            >
                                <!-- Basic Room (Top Level) -->
                                <div
                                    class="basic-room-container"
                                    :class="{'has-children': basicRoomGroup.children.length > 0}"
                                >
                                    <div class="basic-room-row">
                                        <div class="expand-button-container">
                                            <v-btn
                                                v-if="basicRoomGroup.children.length > 0"
                                                icon
                                                size="x-small"
                                                variant="text"
                                                class="expand-btn"
                                                :class="{expanded: isBasicRoomExpanded[basicRoomIndex]}"
                                                @click="toggleBasicRoom(basicRoomIndex)"
                                            >
                                                <v-icon size="20" color="primary">
                                                    {{
                                                        isBasicRoomExpanded[basicRoomIndex]
                                                            ? 'mdi-chevron-down'
                                                            : 'mdi-chevron-right'
                                                    }}
                                                </v-icon>
                                            </v-btn>
                                            <div v-else class="expand-btn-placeholder" />
                                        </div>

                                        <v-checkbox
                                            v-if="basicRoomGroup.basicRoom"
                                            v-model="model.services[basicRoomGroup.basicRoom.modelIndex].checked"
                                            :label="basicRoomGroup.basicRoom.name"
                                            hide-details
                                            density="comfortable"
                                            class="basic-room-checkbox"
                                        />
                                    </div>
                                </div>

                                <!-- Child Rooms (ZERO/SUSTAINABLE) -->
                                <div v-if="isBasicRoomExpanded[basicRoomIndex]" class="children-container">
                                    <div
                                        v-for="childRoom in basicRoomGroup.children"
                                        :key="childRoom.id"
                                        class="child-room-row"
                                    >
                                        <div class="child-room-connector">
                                            <div class="connector-line" />
                                            <v-icon size="12" color="grey-darken-1">
                                                mdi-subdirectory-arrow-right
                                            </v-icon>
                                        </div>

                                        <v-checkbox
                                            v-model="model.services[childRoom.modelIndex].checked"
                                            :label="childRoom.name"
                                            hide-details
                                            density="comfortable"
                                            class="child-room-checkbox"
                                        />

                                        <v-chip
                                            size="x-small"
                                            :color="childRoom.sustainabilityLevel === 'ZERO' ? 'success' : 'info'"
                                            variant="flat"
                                            class="sustainability-badge"
                                        >
                                            {{ childRoom.sustainabilityLevel === 'ZERO' ? 'Zero' : 'Sustainable' }}
                                        </v-chip>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <!-- Fallback to original flat list when no room groups -->
                        <div v-else class="d-flex flex-column">
                            <v-checkbox
                                v-for="(service, index) in services"
                                :key="service.id"
                                v-model="model.services[index].checked"
                                :label="service.name"
                                hide-details
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="12">
                        <v-btn color="primary" class="mr-4" :disabled="!valid()" @click="clickUpdate">
                            {{ $t('supplier.open') }}
                        </v-btn>
                        <v-btn color="primary" :disabled="!valid()" @click="clickClose">
                            {{ $t('supplier.close') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import DateRangeMExtended from '@/components/supplier/DateRangeMExtended.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DaysPicker from '@/components/supplier/DaysPicker.vue'
    import {supplierStore} from '@/composables/store-accessor'

    @Component({
        components: {DaysPicker, DateRangeMExtended},
    })
    export class OpenCloseRooms extends mixins(FormValidationRulesMixin) {
        @Prop() startDate
        @Prop() endDate
        @Prop() services
        @Prop() tariffs

        model = {
            dateFrom: null,
            dateTo: null,
            dayOfWeek: [true, true, true, true, true, true, true],
            services: [],
            operation: null,
            tariffId: null,
        }

        isBasicRoomExpanded = []
        roomTypes = []
        roomTypesLoaded = false
        serviceToRoomTypeMap = new Map()

        async mounted() {
            this.model.dateFrom = this.startDate
            this.model.dateTo = this.endDate

            let services = []
            this.services.forEach(service => {
                services.push({
                    serviceId: service.id,
                    categoryId: service.categoryId,
                    checked: false,
                })
            })
            this.model.services = services

            // Load room types to get sustainability level information
            await this.loadRoomTypes()

            // Initialize expanded state for basic rooms (all collapsed initially)
            this.isBasicRoomExpanded = this.basicRoomGroups.map(() => false)
        }

        async loadRoomTypes() {
            try {
                // First, get the contract ID to load services
                const contractId = supplierStore?.fullContract?.id
                const hotelId = supplierStore?.hotelInfo?.hotelId

                if (!contractId) {
                    this.roomTypesLoaded = true
                    return
                }

                // Load hotel services to get service-to-room-type mapping
                const ownHotelServices = await this.$api.ownHotelServices.get({contractId})

                // Load room types from hotel description
                if (hotelId) {
                    const roomTypesResponse = await this.$api.hotelDescriptionRoomType.get(hotelId, {
                        locale: 'all',
                    })

                    this.roomTypes = roomTypesResponse?.hotelRoomTypes || []

                    // Create mapping from service ID to room type
                    this.serviceToRoomTypeMap = new Map()

                    // ownHotelServices API returns {services: [...]} structure
                    const servicesArray = ownHotelServices?.services || []

                    servicesArray.forEach(service => {
                        if (service.hotelRoomTypeId) {
                            this.serviceToRoomTypeMap.set(service.id, service.hotelRoomTypeId)
                        }
                    })
                }
            } catch (error) {
                console.error('Error loading room types:', error)
                this.roomTypes = []
            } finally {
                this.roomTypesLoaded = true
            }
        }

        get basicRoomGroups() {
            if (!this.services || this.services.length === 0) {
                return []
            }

            const groupsMap = new Map()

            this.services.forEach((service, index) => {
                // Get room type ID from service mapping
                const roomTypeId = this.serviceToRoomTypeMap.get(service.id) || service.hotelRoomTypeId || service.id

                // Find room type data using the mapped ID
                const roomType = this.roomTypes.find(rt => rt.id === roomTypeId)

                let sustainabilityLevel = 'BASIC'
                let basicRoomId: any
                let basicRoomName: any

                if (roomType) {
                    // Use actual room type data
                    sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'

                    if (sustainabilityLevel === 'BASIC') {
                        basicRoomId = roomType.id
                        basicRoomName = roomType.name?.en || roomType.name || service.name
                    } else {
                        // For sustainable/zero rooms, use the basicRoomId to group them
                        basicRoomId = roomType.basicRoomId || roomType.id

                        // Find the basic room name
                        const basicRoom = this.roomTypes.find(rt => rt.id === basicRoomId)
                        basicRoomName = basicRoom?.name?.en || basicRoom?.name || service.name
                    }
                } else {
                    // Fallback: use service data directly or try to detect from name
                    sustainabilityLevel =
                        service.sustainabilityLevel || this.detectSustainabilityFromName(service.name) || 'BASIC'

                    if (sustainabilityLevel === 'BASIC') {
                        basicRoomId = service.id
                        basicRoomName = service.name
                    } else {
                        // Try to find the basic room by removing sustainability keywords from name
                        const baseName = this.extractBaseName(service.name)
                        const basicRoom = this.services.find(s => s.name === baseName || s.name.includes(baseName))
                        basicRoomId = basicRoom?.id || service.id
                        basicRoomName = basicRoom?.name || baseName || service.name
                    }
                }

                if (!groupsMap.has(basicRoomId)) {
                    groupsMap.set(basicRoomId, {
                        basicRoomId,
                        basicRoomName,
                        basicRoom: null,
                        children: [],
                    })
                }

                const group = groupsMap.get(basicRoomId)

                if (sustainabilityLevel === 'BASIC') {
                    group.basicRoom = {
                        ...service,
                        sustainabilityLevel,
                        modelIndex: index,
                    }
                } else {
                    group.children.push({
                        ...service,
                        sustainabilityLevel,
                        modelIndex: index,
                    })
                }
            })

            const result = Array.from(groupsMap.values()).filter(group => group.basicRoom || group.children.length > 0)

            return result
        }

        detectSustainabilityFromName(name: string): string | null {
            const lowerName = name.toLowerCase()
            if (lowerName.includes('zero')) return 'ZERO'
            if (lowerName.includes('sustainable') || lowerName.includes('sustain')) return 'SUSTAINABLE'
            return null
        }

        extractBaseName(name: string): string {
            return name
                .replace(/\s*(zero|sustainable|sustain)\s*/gi, '')
                .replace(/\s+/g, ' ')
                .trim()
        }

        toggleBasicRoom(index: number) {
            this.isBasicRoomExpanded[index] = !this.isBasicRoomExpanded[index]
        }

        valid() {
            const filtered = this.model.services.filter(service => {
                return service.checked === false
            })

            return filtered.length !== this.model.services.length
        }

        clickClose() {
            this.model.operation = 'BLOCK'
            this.$emit('update', this.model)
        }

        clickUpdate() {
            this.model.operation = 'CLEAR_BLOCK'
            this.$emit('update', this.model)
        }
    }

    export default OpenCloseRooms
</script>

<style scoped lang="scss">
    .room-types-tree {
        .basic-room-container {
            margin-bottom: 8px;
            border-radius: 8px;
            transition: all 0.2s ease;

            &.has-children {
                background: rgba(var(--v-theme-surface), 0.5);
                border: 1px solid rgba(var(--v-theme-primary), 0.1);

                &:hover {
                    background: rgba(var(--v-theme-primary), 0.05);
                    border-color: rgba(var(--v-theme-primary), 0.2);
                }
            }

            .basic-room-row {
                display: flex;
                align-items: center;
                padding: 8px 12px;
                gap: 8px;

                .expand-button-container {
                    display: flex;
                    align-items: center;
                    width: 24px;

                    .expand-btn {
                        &:hover {
                            background: rgba(var(--v-theme-primary), 0.1);
                        }
                    }

                    .expand-btn-placeholder {
                        width: 24px;
                        height: 24px;
                    }
                }

                .basic-room-checkbox {
                    flex: 1;

                    :deep(.v-label) {
                        font-weight: 500;
                        color: rgba(var(--v-theme-on-surface), 0.87);
                    }
                }
            }
        }

        .children-container {
            margin-left: 24px;
            border-left: 2px solid rgba(var(--v-theme-primary), 0.1);
            padding-left: 16px;
            margin-bottom: 8px;

            .child-room-row {
                display: flex;
                align-items: center;
                padding: 6px 8px;
                margin-bottom: 4px;
                border-radius: 6px;
                gap: 8px;
                transition: background-color 0.2s ease;

                &:hover {
                    background: rgba(var(--v-theme-surface), 0.8);
                }

                &:last-child {
                    margin-bottom: 0;
                }

                .child-room-connector {
                    display: flex;
                    align-items: center;
                    position: relative;
                    width: 20px;

                    .connector-line {
                        position: absolute;
                        left: -16px;
                        top: 50%;
                        width: 12px;
                        height: 1px;
                        background: rgba(var(--v-theme-primary), 0.3);
                    }
                }

                .child-room-checkbox {
                    flex: 1;

                    :deep(.v-label) {
                        font-size: 0.875rem;
                        color: rgba(var(--v-theme-on-surface), 0.75);
                    }
                }

                .sustainability-badge {
                    margin-left: 8px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
            }
        }
    }

    // Improve checkbox styling
    :deep(.v-selection-control) {
        .v-selection-control__wrapper {
            margin-right: 8px;
        }
    }
</style>
